/**
 * Chat widget configuration types + defaults.
 *
 * Kept separate from the widget's runtime code so other modules (and the
 * top-level `nexor.initChat()` re-export) can pull types without dragging in
 * the DOM construction code.
 */
import type { ChatServerTheme, UUID } from "../types";

export const MESSAGE_MAX_LENGTH = 4000;

export const FIELD_MAX_LENGTH: Record<string, number> = {
  first_name: 200,
  last_name: 200,
  email: 254,
  company: 200,
};

// ─── Public option / handle types ──────────────────────────────────────────

export interface ChatLeadCapture {
  /** Which fields to ask for before chatting. Defaults to ["first_name", "email"]. */
  fields?: Array<"first_name" | "last_name" | "email" | "phone">;
  /** "before" gates chat behind the form; "skip" never asks. Default "before". */
  mode?: "before" | "skip";
  /** Create the lead in Nexor the INSTANT the form is submitted (via POST
   *  /leads), instead of waiting for the visitor's first chat message. Captures
   *  visitors who fill the form but never send a message. Default false. */
  createLeadOnSubmit?: boolean;
  /** Heading rendered above the form (its own view). */
  label?: string;
  /** Submit button label. */
  submitLabel?: string;
  /** Per-field placeholder overrides (for localization). */
  placeholders?: Partial<
    Record<"first_name" | "last_name" | "email" | "phone", string>
  >;
  /** Default country for the phone field — a dial code ("+56") or ISO ("CL").
   *  Sets the initial flag, prefix and max length. */
  phoneCountryCode?: string;
  /** Restrict the phone country selector to these ISO codes (e.g. ["CL","AR"]),
   *  in this order. Omit to offer the full built-in list. */
  phoneCountries?: string[];
  /** Validation error copy (for localization). */
  errorMessages?: {
    required?: string;
    email?: string;
    phone?: string;
  };
  /** Optional, explicit SMS opt-in shown below the phone field. The checkbox is
   *  always unchecked and never required to continue. When configured, the SDK
   *  sends an immutable consent/decline event with the created lead. */
  smsConsent?: ChatSmsConsent;
}

export interface ChatSmsConsent {
  /** Render the optional consent control. Defaults to false. */
  enabled?: boolean;
  /** Messaging program / sender brand exactly as recipients will see it. */
  programName: string;
  /** Version controlled by the embedder (for example "sms-2026-08-04"). */
  disclosureVersion: string;
  /** Complete disclosure shown next to the unchecked checkbox. Include message
   *  frequency, message/data rates, STOP/HELP, and that consent is optional. */
  disclosureText: string;
  /** Public, same-domain privacy policy URL. */
  privacyPolicyUrl: string;
  /** Public, same-domain terms URL. */
  termsUrl: string;
  /** Link labels. Localizable; defaults to English/Spanish built-in copy. */
  privacyLabel?: string;
  termsLabel?: string;
}

/** The actions that can appear in the hover/speed-dial fan above the launcher. */
export type ChatChannelKey = "whatsapp" | "call" | "email" | "instagram" | "chat";

/** Channels that can open the in-widget contact-request form. */
export type ContactRequestChannel = "call" | "email" | "whatsapp";

/**
 * Turns the `call` / `email` (optionally `whatsapp`) fan buttons into an
 * in-widget contact-request FORM instead of a tel:/mailto: link. The visitor
 * fills name + email + phone, and on submit the widget creates a lead in Nexor
 * and forces the FIRST contact to go out on that channel (the workflow's agent
 * composes the message), with context that they requested it from the website.
 *
 * All copy is per-channel so it can be localized; sensible English defaults
 * apply when omitted.
 */
export interface ChatRequestContact {
  /** Fan channels that open the form instead of navigating. Default ["call","email"]. */
  channels?: ContactRequestChannel[];
  /** Heading above the form, per channel. */
  title?: Partial<Record<ContactRequestChannel, string>>;
  /** Explainer line (e.g. "we need this info to contact you"), per channel. */
  subtitle?: Partial<Record<ContactRequestChannel, string>>;
  /** Submit button label, per channel. */
  submitLabel?: Partial<Record<ContactRequestChannel, string>>;
  /** Confirmation shown after a successful request, per channel. */
  successText?: Partial<Record<ContactRequestChannel, string>>;
  /** Label for the button that dismisses the success screen. Default "Done". */
  doneLabel?: string;
  /** Error shown when the request fails. */
  errorText?: string;
}

/**
 * Optional "speed-dial" channels shown when the visitor hovers (desktop) or taps
 * (touch) the launcher bubble. Instead of opening the chat immediately, the
 * launcher fans out a column of round buttons — WhatsApp, call, email, Instagram,
 * and an "open chat" button — letting the visitor pick a channel.
 *
 * If only `chat` would be enabled (no other channel supplied), the fan is skipped
 * and the launcher keeps its default behaviour (click opens the chat).
 */
export interface ChatChannels {
  /** WhatsApp number in international format (e.g. "+56912345678"). Opens wa.me. */
  whatsapp?: string;
  /** Pre-filled WhatsApp message text. */
  whatsappText?: string;
  /** Phone number for the call button (e.g. "+56912345678"). Opens tel:. */
  call?: string;
  /** Email address. Opens mailto:. */
  email?: string;
  /** Instagram handle ("nexor.ai") or full profile URL. */
  instagram?: string;
  /** Include a button that opens the chat panel. Defaults to true. */
  chat?: boolean;
  /** Top-to-bottom order of the fan buttons. Defaults to the order below. */
  order?: ChatChannelKey[];
  /** Accessible labels / hover tooltips per channel. */
  labels?: Partial<Record<ChatChannelKey, string>>;
  /** Turn `call`/`email` (and optionally `whatsapp`) into an in-widget
   *  contact-request form that creates a lead and forces first contact on that
   *  channel, instead of opening a tel:/mailto: link. Omit to keep magic links. */
  requestContact?: ChatRequestContact;
}

export interface ChatInitOptions {
  /** Workflow that handles this chat conversation. Required. */
  workflowId: UUID;

  // ── Identity / branding ──
  title?: string;
  subtitle?: string;
  /** Header avatar/logo image URL. Falls back to the title's initials. */
  avatarUrl?: string;
  /** Suggested first questions shown as tappable chips under the greeting
   *  (3-4 max recommended). Tapping one sends it as the visitor's message. */
  suggestions?: string[];
  /** Two-line headline for the Home view's gradient hero: [greetingLine,
   *  titleLine] (e.g. ["Hola,", "¿En qué te ayudamos?"]). Defaults from
   *  UI_STRINGS per language. */
  homeTitle?: [string, string];
  /**
   * Force the opening message for this widget instance. Highest priority —
   * WINS over the dashboard `initial_message` and over `greeting`. Use it for
   * per-page openings (e.g. an ecommerce-specific greeting on /ecommerce).
   * Supports the same {{first_name}} / {{last_name}} / {{full_name}} / {{email}}
   * / {{phone}} tokens, substituted from the captured/known lead.
   */
  openingMessage?: string;
  /** Fallback opening, used only when neither `openingMessage` nor the
   *  dashboard `initial_message` is set. */
  greeting?: string;
  /** Shown when a turn fails or the server returns no reply. */
  errorText?: string;
  /** Shown when web chat is disabled for the workflow (channel turned off in
   *  the dashboard, or the API reports it unavailable). */
  disabledText?: string;
  /** BCP-47 locale for built-in UI copy and timestamps (e.g. "es", "en-US").
   *  Defaults to the browser. Spanish-family locales get Spanish defaults; all
   *  explicit title/capture/error/etc. options still take priority. */
  locale?: string;
  /** UI copy language ("es" | "en"). Defaults from `locale`/navigator.language. */
  language?: "es" | "en";
  /** Title of the Home "send us a message" card. */
  homeCardTitle?: string;
  /** Agent display name for the chat header (falls back to title). */
  agentName?: string;
  /** Agent presence shown in the chat header. Default "online". */
  presence?: "online" | "away" | "offline";
  /** Hex colour for bubble + accents. Defaults to #111827. */
  accentColor?: string;
  /** Text colour rendered on top of the accent. Defaults to white. */
  accentTextColor?: string;
  /** Show the "Powered by Nexor" footer. Defaults to true. */
  showBranding?: boolean;
  /** Composer placeholder once chat is active. Default "Type a message…". */
  inputPlaceholder?: string;
  /** Show a header "start over" control that wipes the conversation and starts a
   *  BRAND-NEW lead (the backend won't re-match the old one). Default true. */
  showRestart?: boolean;
  /** aria-label / tooltip for the header restart control. Default "Start over". */
  restartLabel?: string;

  // ── Behaviour ──
  position?: "bottom-right" | "bottom-left";
  /** Corner style for the panel, bubbles and controls. "square" trims the
   *  radii down to a tighter, more angular look. Default "rounded". */
  shape?: "rounded" | "square";
  /** Auto-open the panel on first load. Defaults to false. */
  openOnLoad?: boolean;
  /**
   * Turn the launcher into a hover/tap "speed-dial" that fans out channel
   * buttons (WhatsApp, call, email, Instagram, chat) instead of opening the
   * chat right away. Omit for the classic click-to-open launcher.
   */
  channels?: ChatChannels;
  /** Coalesce rapid-fire messages: wait this many ms of quiet, then send the
   *  buffered messages as one combined turn. Defaults to 700. 0 = per message. */
  debounceMs?: number;
  /** Split a bot reply into separate bubbles on blank lines (paragraph breaks),
   *  WhatsApp-style. Defaults to true. Set false to keep one bubble per reply. */
  splitReplies?: boolean;
  /** Fixed typing pause (ms) shown between split bubbles. Omit for a natural
   *  3000–5000ms pause scaled by the length of the next message (longer text
   *  reads as more time spent typing). */
  splitDelayMs?: number;
  /** Per-turn request timeout (ms). Tool-heavy agent turns can take several
   *  minutes, so the default is 360000. Chat turns never retry — a timeout
   *  surfaces the error once rather than re-sending the message. */
  requestTimeoutMs?: number;
  /** Append the widget to a custom element. Defaults to document.body. */
  container?: HTMLElement;

  /** Force iframe-embedded mode: turns go through the origin-bound
   *  `/api/widget/v1/turn` surface (parent origin validated against the
   *  workflow's allowed origins) instead of `/api/public/chat`. Omit to
   *  auto-detect via `window.self !== window.top`. */
  embedded?: boolean;

  // ── Prompt injection forwarded to the agent for THIS widget instance ──
  // ADDITIVE: the API appends these to the workflow prompt (wrapped in
  // <page_context>), it does NOT replace it. Use for per-page context, e.g. on
  // /ecommerce: "The visitor is browsing ecommerce; focus on cart recovery…".
  systemPrompt?: string;
  clientPrompt?: string;

  // ── Lead linking ──
  /** Pre-known lead info (e.g. a logged-in user). Skips capture if complete. */
  lead?: {
    first_name?: string;
    last_name?: string;
    email?: string;
    phone?: string;
    metadata?: Record<string, unknown>;
  };
  capture?: ChatLeadCapture;
  /** Arbitrary metadata passed with each turn (page URL, UTM, …). Pass a
   *  FUNCTION to have it resolved fresh on every turn — useful for SPA values
   *  that change without re-mounting the widget (e.g. the current route). */
  metadata?:
    | Record<string, unknown>
    | (() => Record<string, unknown> | undefined);

  // ── Returning-visitor resume banner ──
  /** Banner text when a prior conversation is restored but we have no name.
   *  Default "Continuing your conversation". */
  resumeText?: string;
  /** Banner text when we know the visitor. `{name}` is replaced with their
   *  first name (or email / phone as a fallback). Default "Continuing as {name}". */
  resumeWithNameText?: string;
  /** Label for the reset action in the resume banner. Default "Start over". */
  startOverText?: string;

  // ── Callbacks ──
  onOpen?: () => void;
  onClose?: () => void;
  onMessage?: (msg: { role: "user" | "bot"; text: string }) => void;
  onLeadCaptured?: (leadId: UUID) => void;
  onError?: (err: Error) => void;
}

export interface ChatHandle {
  open(): void;
  close(): void;
  toggle(): void;
  /** Programmatically send a user message. */
  send(text: string): Promise<void>;
  /** Update per-turn context (page prompt / opening) WITHOUT remounting — for
   *  SPA navigation where only the page changes. No flicker. */
  update(patch: { clientPrompt?: string; systemPrompt?: string; openingMessage?: string }): void;
  /** Tear down the widget and remove all DOM. */
  destroy(): void;
  /** Read the current session id (auto-generated on first open). */
  getSessionId(): string;
}

// ─── Internal resolved-config type ─────────────────────────────────────────

/** Fully-resolved capture config — every field present, no `undefined`. */
export interface ResolvedCapture {
  fields: Array<"first_name" | "last_name" | "email" | "phone">;
  mode: "before" | "skip";
  createLeadOnSubmit: boolean;
  label: string;
  submitLabel: string;
  placeholders: Partial<
    Record<"first_name" | "last_name" | "email" | "phone", string>
  >;
  phoneCountryCode: string;
  phoneCountries: string[];
  errorMessages: { required: string; email: string; phone: string };
  smsConsent: ResolvedSmsConsent | null;
}

export interface ResolvedSmsConsent {
  programName: string;
  disclosureVersion: string;
  disclosureText: string;
  privacyPolicyUrl: string;
  termsUrl: string;
  privacyLabel: string;
  termsLabel: string;
}

/** One ready-to-render fan button: an icon key + an href (null = open chat). */
export interface ResolvedChannel {
  key: ChatChannelKey;
  /** Navigation target. `null` for JS-handled actions (chat + contact form). */
  href: string | null;
  /** Open in a new tab? (WhatsApp / Instagram). tel:/mailto: stay in place. */
  external: boolean;
  label: string;
  /** JS-handled action: "contact" opens the contact-request form (the `chat`
   *  key opens the panel). Absent → a plain navigation link. */
  action?: "contact";
}

/** Fully-resolved contact-request config (every field present). */
export interface ResolvedRequestContact {
  channels: ContactRequestChannel[];
  title: Partial<Record<ContactRequestChannel, string>>;
  subtitle: Partial<Record<ContactRequestChannel, string>>;
  submitLabel: Partial<Record<ContactRequestChannel, string>>;
  successText: Partial<Record<ContactRequestChannel, string>>;
  doneLabel: string;
  errorText: string;
}

export interface ResolvedCfg {
  /** The raw options the embedder passed to `initChat()`, BEFORE defaults were
   *  applied. Used to tell "the embedder set this explicitly" (wins) apart
   *  from "this is just a default" (the server-authored theme may override
   *  it) — see `applyServerTheme` in index.ts. */
  raw: ChatInitOptions;
  workflowId: UUID;
  title: string;
  subtitle: string;
  avatarUrl?: string;
  suggestions: string[];
  homeTitle: [string, string];
  homeCardTitle: string;
  agentName: string;
  presence: "online" | "away" | "offline";
  presenceLabels: { online: string; away: string; offline: string };
  aria: { close: string; back: string; send: string; country: string; poweredBy: string };
  downloadFileLabel: string;
  rateLimitedText: string;
  openingMessage?: string;
  greeting: string;
  errorText: string;
  disabledText: string;
  locale?: string;
  accentColor: string;
  accentTextColor: string;
  showBranding: boolean;
  inputPlaceholder: string;
  showRestart: boolean;
  restartLabel: string;
  position: "bottom-right" | "bottom-left";
  shape: "rounded" | "square";
  openOnLoad: boolean;
  /** Resolved fan buttons. Empty array → classic launcher (no fan). */
  channels: ResolvedChannel[];
  /** Resolved contact-request copy/behaviour (undefined when not configured). */
  requestContact?: ResolvedRequestContact;
  debounceMs: number;
  splitReplies: boolean;
  splitDelayMs: number;
  requestTimeoutMs: number;
  container?: HTMLElement;
  embedded?: boolean;
  systemPrompt?: string;
  clientPrompt?: string;
  lead?: ChatInitOptions["lead"];
  capture: ResolvedCapture;
  metadata?:
    | Record<string, unknown>
    | (() => Record<string, unknown> | undefined);
  resumeText: string;
  resumeWithNameText: string;
  startOverText: string;
  onOpen?: () => void;
  onClose?: () => void;
  onMessage?: (msg: { role: "user" | "bot"; text: string }) => void;
  onLeadCaptured?: (leadId: UUID) => void;
  onError?: (err: Error) => void;
}

interface ChatDefaultCopy {
  title: string;
  subtitle: string;
  greeting: string;
  errorText: string;
  disabledText: string;
  inputPlaceholder: string;
  restartLabel: string;
  resumeText: string;
  resumeWithNameText: string;
  startOverText: string;
  lockedPlaceholder: string;
  launcherContactLabel: string;
  launcherOpenLabel: string;
  closeLabel: string;
  countryCodeLabel: string;
  sendLabel: string;
  capture: {
    label: string;
    submitLabel: string;
    placeholders: Record<"first_name" | "last_name" | "email" | "phone", string>;
    errorMessages: { required: string; email: string; phone: string };
    smsConsentLinks: { privacy: string; terms: string };
  };
  channelLabels: Record<ChatChannelKey, string>;
  contact: {
    title: Record<ContactRequestChannel, string>;
    subtitle: Record<ContactRequestChannel, string>;
    submitLabel: Record<ContactRequestChannel, string>;
    successText: Record<ContactRequestChannel, string>;
    doneLabel: string;
    errorText: string;
  };
}

const EN_CHAT_COPY: ChatDefaultCopy = {
  title: "Chat with us",
  subtitle: "We typically reply in a few minutes",
  greeting: "Hi there! How can we help?",
  errorText: "Sorry, something went wrong. Please try again in a moment.",
  disabledText: "Chat isn't available right now. Please reach us on another channel.",
  inputPlaceholder: "Type a message…",
  restartLabel: "Start over",
  resumeText: "Continuing your conversation",
  resumeWithNameText: "Continuing as {name}",
  startOverText: "Start over",
  lockedPlaceholder: "Complete the form above to start…",
  launcherContactLabel: "Contact us",
  launcherOpenLabel: "Open chat",
  closeLabel: "Close chat",
  countryCodeLabel: "Country code",
  sendLabel: "Send message",
  capture: {
    label: "Tell us a bit about you to get started:",
    submitLabel: "Start chat",
    placeholders: {
      first_name: "First name",
      last_name: "Last name",
      email: "Email",
      phone: "Phone",
    },
    errorMessages: {
      required: "Please complete this field.",
      email: "Enter a valid email address.",
      phone: "Enter a valid phone number, including the country code.",
    },
    smsConsentLinks: { privacy: "Privacy Policy", terms: "Terms" },
  },
  channelLabels: {
    whatsapp: "WhatsApp",
    call: "Call",
    email: "Email",
    instagram: "Instagram",
    chat: "Chat",
  },
  contact: {
    title: {
      call: "Request a call",
      email: "Request an email",
      whatsapp: "Request a message",
    },
    subtitle: {
      call: "Leave your details and we'll call you. We need this to reach you.",
      email: "Leave your details and we'll email you. We need this to reach you.",
      whatsapp: "Leave your details and we'll message you. We need this to reach you.",
    },
    submitLabel: {
      call: "Request call",
      email: "Request email",
      whatsapp: "Request message",
    },
    successText: {
      call: "Thanks! We'll call you shortly.",
      email: "Thanks! We'll email you shortly.",
      whatsapp: "Thanks! We'll message you shortly.",
    },
    doneLabel: "Done",
    errorText: "Something went wrong. Please try again.",
  },
};

const ES_CHAT_COPY: ChatDefaultCopy = {
  title: "Habla con nosotros",
  subtitle: "Normalmente respondemos en pocos minutos",
  greeting: "¡Hola! ¿En qué podemos ayudarte?",
  errorText: "Lo sentimos, ocurrió un error. Inténtalo de nuevo en un momento.",
  disabledText: "El chat no está disponible ahora. Contáctanos por otro canal.",
  inputPlaceholder: "Escribe un mensaje…",
  restartLabel: "Empezar de nuevo",
  resumeText: "Continuando tu conversación",
  resumeWithNameText: "Continuando como {name}",
  startOverText: "Empezar de nuevo",
  lockedPlaceholder: "Completa el formulario de arriba para comenzar…",
  launcherContactLabel: "Contáctanos",
  launcherOpenLabel: "Abrir chat",
  closeLabel: "Cerrar chat",
  countryCodeLabel: "Código de país",
  sendLabel: "Enviar mensaje",
  capture: {
    label: "Cuéntanos un poco sobre ti para comenzar:",
    submitLabel: "Iniciar chat",
    placeholders: {
      first_name: "Nombre",
      last_name: "Apellido",
      email: "Correo electrónico",
      phone: "Teléfono",
    },
    errorMessages: {
      required: "Completa este campo.",
      email: "Ingresa un correo electrónico válido.",
      phone: "Ingresa un teléfono válido, incluido el código de país.",
    },
    smsConsentLinks: { privacy: "Política de Privacidad", terms: "Términos" },
  },
  channelLabels: {
    whatsapp: "WhatsApp",
    call: "Llamar",
    email: "Correo",
    instagram: "Instagram",
    chat: "Chat",
  },
  contact: {
    title: {
      call: "Solicitar una llamada",
      email: "Solicitar un correo",
      whatsapp: "Solicitar un mensaje",
    },
    subtitle: {
      call: "Déjanos tus datos y te llamaremos.",
      email: "Déjanos tus datos y te escribiremos por correo.",
      whatsapp: "Déjanos tus datos y te escribiremos por WhatsApp.",
    },
    submitLabel: {
      call: "Solicitar llamada",
      email: "Solicitar correo",
      whatsapp: "Solicitar mensaje",
    },
    successText: {
      call: "¡Gracias! Te llamaremos pronto.",
      email: "¡Gracias! Te escribiremos pronto.",
      whatsapp: "¡Gracias! Te enviaremos un mensaje pronto.",
    },
    doneLabel: "Listo",
    errorText: "Ocurrió un error. Inténtalo de nuevo.",
  },
};

/** Built-in widget copy follows an explicit locale, then the browser locale. */
export function getChatDefaultCopy(locale?: string): ChatDefaultCopy {
  return /^es(?:[-_]|$)/i.test(locale ?? "") ? ES_CHAT_COPY : EN_CHAT_COPY;
}

/**
 * Normalize user-provided options into a complete config object with every
 * field set. Downstream code reads from ResolvedCfg and never has to deal
 * with `undefined` for branding / behaviour fields.
 */

// ─── UI strings (ES/EN) ────────────────────────────────────────────────────
// Todo copy default del widget vive aqui; el idioma se resuelve con
// `language` explicito → navigator.language del visitante → en.
const UI_STRINGS = {
  en: {
    homeTitle: ["Hi there,", "How can we help?"] as [string, string],
    homeCardTitle: "Send us a message",
    presence: { online: "Online", away: "Away", offline: "Offline" },
    aria: { close: "Close chat", back: "Back to home", send: "Send message", country: "Country code", poweredBy: "Powered by" },
    downloadFile: "Download file",
    rateLimited: "You're sending messages too quickly — give it a moment and try again.",
  },
  es: {
    homeTitle: ["Hola,", "¿En qué te ayudamos?"] as [string, string],
    homeCardTitle: "Envíanos un mensaje",
    presence: { online: "En línea", away: "Ausente", offline: "Desconectado" },
    aria: { close: "Cerrar chat", back: "Volver al inicio", send: "Enviar mensaje", country: "Código de país", poweredBy: "Powered by" },
    downloadFile: "Descargar archivo",
    rateLimited: "Estás enviando mensajes muy rápido — espera un momento y vuelve a intentar.",
  },
} as const;

export function resolveUiLang(o: ChatInitOptions): "es" | "en" {
  if (o.language === "es" || o.language === "en") return o.language;
  const loc = o.locale ?? (typeof navigator !== "undefined" ? navigator.language : "");
  return /^es/i.test(loc || "") ? "es" : "en";
}

export function applyDefaults(o: ChatInitOptions): ResolvedCfg {
  const S = UI_STRINGS[resolveUiLang(o)];
  const locale =
    o.locale ??
    (typeof navigator !== "undefined" ? navigator.language : undefined);
  const copy = getChatDefaultCopy(locale);

  const smsConsent = resolveSmsConsent(o.capture?.smsConsent, copy);
  const captureFields = o.capture?.fields ?? ["first_name", "email"];
  if (smsConsent && !captureFields.includes("phone")) {
    throw new Error("Nexor SDK: capture.smsConsent requires capture.fields to include phone.");
  }

  return {
    raw: o,
    workflowId: o.workflowId,
    title: o.title ?? copy.title,
    subtitle: o.subtitle ?? copy.subtitle,
    avatarUrl: o.avatarUrl,
    suggestions: o.suggestions ?? [],
    homeTitle: o.homeTitle ?? S.homeTitle,
    homeCardTitle: o.homeCardTitle ?? S.homeCardTitle,
    agentName: o.agentName ?? o.title ?? copy.title,
    presence: o.presence ?? "online",
    presenceLabels: S.presence,
    aria: S.aria,
    downloadFileLabel: S.downloadFile,
    rateLimitedText: S.rateLimited,
    openingMessage: o.openingMessage,
    greeting: o.greeting ?? copy.greeting,
    errorText: o.errorText ?? copy.errorText,
    disabledText: o.disabledText ?? copy.disabledText,
    locale,
    accentColor: o.accentColor ?? "#111827",
    accentTextColor: o.accentTextColor ?? "#ffffff",
    showBranding: o.showBranding ?? true,
    inputPlaceholder: o.inputPlaceholder ?? copy.inputPlaceholder,
    showRestart: o.showRestart ?? true,
    restartLabel: o.restartLabel ?? copy.restartLabel,
    position: o.position ?? "bottom-right",
    shape: o.shape ?? "rounded",
    openOnLoad: o.openOnLoad ?? false,
    channels: resolveChannels(o.channels, locale),
    requestContact: resolveRequestContact(o.channels?.requestContact, locale),
    debounceMs: o.debounceMs ?? 700,
    splitReplies: o.splitReplies ?? true,
    splitDelayMs: o.splitDelayMs ?? 0, // 0 = auto (random 1000–1500ms per bubble)
    requestTimeoutMs: o.requestTimeoutMs ?? 360_000,
    container: o.container,
    embedded: o.embedded,
    systemPrompt: o.systemPrompt,
    clientPrompt: o.clientPrompt,
    lead: o.lead,
    capture: {
      fields: captureFields,
      mode: o.capture?.mode ?? "before",
      createLeadOnSubmit: o.capture?.createLeadOnSubmit ?? false,
      label: o.capture?.label ?? copy.capture.label,
      submitLabel: o.capture?.submitLabel ?? copy.capture.submitLabel,
      placeholders: { ...copy.capture.placeholders, ...(o.capture?.placeholders ?? {}) },
      phoneCountryCode: o.capture?.phoneCountryCode ?? "",
      phoneCountries: o.capture?.phoneCountries ?? [],
      errorMessages: {
        required:
          o.capture?.errorMessages?.required ?? copy.capture.errorMessages.required,
        email: o.capture?.errorMessages?.email ?? copy.capture.errorMessages.email,
        phone: o.capture?.errorMessages?.phone ?? copy.capture.errorMessages.phone,
      },
      smsConsent,
    },
    metadata: o.metadata,
    resumeText: o.resumeText ?? copy.resumeText,
    resumeWithNameText: o.resumeWithNameText ?? copy.resumeWithNameText,
    startOverText: o.startOverText ?? copy.startOverText,
    onOpen: o.onOpen,
    onClose: o.onClose,
    onMessage: o.onMessage,
    onLeadCaptured: o.onLeadCaptured,
    onError: o.onError,
  };
}

function resolveSmsConsent(
  raw: ChatSmsConsent | undefined,
  copy: ChatDefaultCopy,
): ResolvedSmsConsent | null {
  if (!raw?.enabled) return null;
  const required: Array<keyof ChatSmsConsent> = [
    "programName",
    "disclosureVersion",
    "disclosureText",
    "privacyPolicyUrl",
    "termsUrl",
  ];
  const missing = required.filter((key) => !String(raw[key] ?? "").trim());
  if (missing.length) {
    throw new Error(`Nexor SDK: capture.smsConsent is missing ${missing.join(", ")}.`);
  }
  for (const key of ["privacyPolicyUrl", "termsUrl"] as const) {
    try {
      const url = new URL(raw[key]);
      if (url.protocol !== "https:" && url.protocol !== "http:") throw new Error();
    } catch {
      throw new Error(`Nexor SDK: capture.smsConsent.${key} must be a public HTTP(S) URL.`);
    }
  }
  return {
    programName: raw.programName.trim(),
    disclosureVersion: raw.disclosureVersion.trim(),
    disclosureText: raw.disclosureText.trim(),
    privacyPolicyUrl: raw.privacyPolicyUrl,
    termsUrl: raw.termsUrl,
    privacyLabel: raw.privacyLabel?.trim() || copy.capture.smsConsentLinks.privacy,
    termsLabel: raw.termsLabel?.trim() || copy.capture.smsConsentLinks.terms,
  };
}

export function seedFromCache(cfg: ResolvedCfg, theme?: ChatServerTheme): ResolvedCfg {
  if (!theme) return cfg;
  const o = cfg.raw;
  if (theme.accent_color && o.accentColor === undefined) cfg.accentColor = theme.accent_color;
  if (theme.shape && o.shape === undefined) cfg.shape = theme.shape;
  if (theme.agent_name && o.agentName === undefined) cfg.agentName = theme.agent_name;
  if (theme.title && o.title === undefined) cfg.title = theme.title;
  if (theme.subtitle && o.subtitle === undefined) cfg.subtitle = theme.subtitle;
  if (theme.home_title && o.homeTitle === undefined) cfg.homeTitle = theme.home_title;
  if (theme.home_card_title && o.homeCardTitle === undefined) {
    cfg.homeCardTitle = theme.home_card_title;
  }
  if (theme.avatar_url && o.avatarUrl === undefined) cfg.avatarUrl = theme.avatar_url;
  if (theme.suggestions?.length && o.suggestions === undefined) {
    cfg.suggestions = theme.suggestions;
  }
  if (theme.capture_fields?.length && o.capture === undefined) {
    cfg.capture.fields = theme.capture_fields;
  }
  return cfg;
}

// ─── Channel (speed-dial) resolution ───────────────────────────────────────

const DEFAULT_CHANNEL_ORDER: ChatChannelKey[] = [
  "whatsapp",
  "call",
  "email",
  "instagram",
  "chat",
];

/**
 * Turn the user's `channels` option into a flat, ordered list of fan buttons.
 *
 * A button is only included when its underlying value is present (a WhatsApp
 * number, a phone number, …). The chat button is included unless explicitly
 * disabled. If the result would be "chat only" (no real channel to dial), the
 * fan is pointless, so we return `[]` and the launcher keeps its classic
 * click-to-open behaviour.
 */
export function resolveChannels(c?: ChatChannels, locale?: string): ResolvedChannel[] {
  if (!c) return [];
  const order = c.order ?? DEFAULT_CHANNEL_ORDER;
  const defaults = getChatDefaultCopy(locale).channelLabels;
  const label = (k: ChatChannelKey) =>
    c.labels?.[k] ?? defaults[k];

  // Channels routed through the in-widget contact-request form (JS-handled),
  // not a tel:/mailto:/wa.me link. A contact channel shows its button even
  // without an underlying value — the form collects the VISITOR's details.
  const contactChannels = new Set<ContactRequestChannel>(
    c.requestContact ? c.requestContact.channels ?? ["call", "email"] : [],
  );
  const contactBtn = (key: ChatChannelKey): ResolvedChannel => ({
    key,
    href: null,
    external: false,
    action: "contact",
    label: label(key),
  });

  // De-dupe while preserving the requested order.
  const seen = new Set<ChatChannelKey>();
  const out: ResolvedChannel[] = [];

  for (const key of order) {
    if (seen.has(key)) continue;
    seen.add(key);

    switch (key) {
      case "whatsapp": {
        if (contactChannels.has("whatsapp")) { out.push(contactBtn(key)); break; }
        if (!c.whatsapp) break;
        const digits = c.whatsapp.replace(/[^\d]/g, "");
        if (!digits) break;
        const qs = c.whatsappText
          ? `?text=${encodeURIComponent(c.whatsappText)}`
          : "";
        out.push({ key, href: `https://wa.me/${digits}${qs}`, external: true, label: label(key) });
        break;
      }
      case "call": {
        if (contactChannels.has("call")) { out.push(contactBtn(key)); break; }
        if (!c.call) break;
        const tel = c.call.replace(/[^\d+]/g, "");
        if (!tel) break;
        out.push({ key, href: `tel:${tel}`, external: false, label: label(key) });
        break;
      }
      case "email": {
        if (contactChannels.has("email")) { out.push(contactBtn(key)); break; }
        if (!c.email) break;
        out.push({ key, href: `mailto:${c.email}`, external: false, label: label(key) });
        break;
      }
      case "instagram": {
        if (!c.instagram) break;
        const href = /^https?:\/\//i.test(c.instagram)
          ? c.instagram
          : `https://instagram.com/${c.instagram.replace(/^@/, "")}`;
        out.push({ key, href, external: true, label: label(key) });
        break;
      }
      case "chat": {
        if (c.chat === false) break;
        out.push({ key, href: null, external: false, label: label(key) });
        break;
      }
    }
  }

  // "chat only" → no real channel to dial, so the fan adds nothing. Fall back
  // to the classic launcher.
  if (out.every((ch) => ch.key === "chat")) return [];
  return out;
}

/**
 * Resolve the contact-request config, filling locale-aware defaults. Returns
 * undefined when the embedder didn't opt in (so the fan keeps magic links).
 */
export function resolveRequestContact(
  rc?: ChatRequestContact,
  locale?: string,
): ResolvedRequestContact | undefined {
  if (!rc) return undefined;
  const defaults = getChatDefaultCopy(locale).contact;
  const channels: ContactRequestChannel[] =
    rc.channels && rc.channels.length ? rc.channels : ["call", "email"];
  const pick = (
    over: Partial<Record<ContactRequestChannel, string>> | undefined,
    def: Record<ContactRequestChannel, string>,
  ): Partial<Record<ContactRequestChannel, string>> => {
    const out: Partial<Record<ContactRequestChannel, string>> = {};
    for (const ch of channels) out[ch] = over?.[ch] ?? def[ch];
    return out;
  };
  return {
    channels,
    title: pick(rc.title, defaults.title),
    subtitle: pick(rc.subtitle, defaults.subtitle),
    submitLabel: pick(rc.submitLabel, defaults.submitLabel),
    successText: pick(rc.successText, defaults.successText),
    doneLabel: rc.doneLabel ?? defaults.doneLabel,
    errorText: rc.errorText ?? defaults.errorText,
  };
}

/**
 * Does this widget need to show a lead-capture form before chat?
 *   - "skip" mode → no
 *   - Pre-known lead already covers every required field → no
 *   - Otherwise → yes
 */
export function needsCapture(cfg: ResolvedCfg): boolean {
  if (cfg.capture.mode === "skip") return false;
  if (cfg.lead) {
    const have = cfg.lead;
    return cfg.capture.fields.some((f) => !have[f]);
  }
  return true;
}
